import React, { useState, useCallback, useMemo } from 'react';
import InvoiceItemListBootstrap from './QuoteItemListBootstrap';
import { getValidationState } from '../utils/quoteValidation';

const QuoteFormBootstrap = ({
  formData,
  onFormDataChange,
  availableArticles,
  loading = false,
  invoiceSettings,
  validationErrors = {}
}) => {
  // Track which fields have been touched for validation display
  const [touchedFields, setTouchedFields] = useState({});

  // Mark field as touched
  const handleFieldTouch = useCallback((fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  }, []);

  // Handle items change
  const handleItemsChange = useCallback((items) => {
    handleFieldTouch('items');
    onFormDataChange({ items });
  }, [onFormDataChange, handleFieldTouch]);

  // Handle rounding change
  const handleRoundingChange = useCallback((roundValue) => {
    onFormDataChange({ roundTotal: roundValue });
  }, [onFormDataChange]);

  // Handle date change with validation
  const handleDateChange = useCallback((e, dateType) => {
    handleFieldTouch(dateType);
    try {
      const date = new Date(e.target.value);
      if (!isNaN(date.getTime())) {
        onFormDataChange({ [dateType]: date });
      }
    } catch (error) {
      console.error('Invalid date:', error);
    }
  }, [onFormDataChange, handleFieldTouch]);

  // Handle text field changes
  const handleFieldChange = useCallback((fieldName, value) => {
    handleFieldTouch(fieldName);
    onFormDataChange({ [fieldName]: value });
  }, [onFormDataChange, handleFieldTouch]);

  // Format date for input - use local date to avoid timezone offset issues
  const formatDate = useCallback((date) => {
    if (!date) return '';
    try {
      if (date instanceof Date && !isNaN(date)) {
        // Use local date components to avoid UTC conversion issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    } catch (e) {
      return '';
    }
  }, []);

  // Get validation props for form fields
  const getFieldValidation = useCallback((fieldName) => {
    return getValidationState(fieldName, validationErrors, touchedFields);
  }, [validationErrors, touchedFields]);

  // Currency options for the dropdown
  const currencyOptions = useMemo(() => [
    { value: 'SEK', label: 'SEK - Svenska kronor' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'NOK', label: 'NOK - Norska kronor' },
    { value: 'DKK', label: 'DKK - Danska kronor' }
  ], []);


  return (
    <>
      <style>{`
      .invoice-form-container {
        max-width: 100%;
        animation: fadeIn 0.3s ease-in;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .invoice-form-header {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 1.5rem;
        margin-bottom: 2rem;
      }
      
      .invoice-form-header-content {
        display: flex;
        align-items: center;
        gap: 1rem;
      }
      
      .invoice-form-icon {
        width: 48px;
        height: 48px;
        background: #0f172a;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.25rem;
        flex-shrink: 0;
      }
      
      .invoice-form-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
      }
      
      .invoice-form-subtitle {
        font-size: 0.875rem;
        color: #64748b;
        margin: 0.25rem 0 0 0;
        line-height: 1.4;
      }
      
      .form-section {
        background: white;
        border-radius: 12px;
        padding: 2rem;
        margin-bottom: 1.5rem;
        border: 1px solid #e5e7eb;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
      
      .section-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #f1f5f9;
      }
      
      .section-icon {
        width: 40px;
        height: 40px;
        background: #f0f9ff;
        border: 1px solid #0284c7;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0284c7;
        font-size: 1.25rem;
      }
      
      .section-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
      }
      
      .section-subtitle {
        font-size: 0.875rem;
        color: #64748b;
        margin: 0.25rem 0 0 0;
      }
      
      .form-label-modern {
        display: block;
        font-size: 0.875rem;
        font-weight: 600;
        color: #374151;
        margin-bottom: 0.5rem;
      }
      
      .required-asterisk {
        color: #dc2626;
        margin-left: 0.25rem;
      }
      
      .form-help {
        font-size: 0.75rem;
        color: #6b7280;
        margin-left: 0.5rem;
        font-weight: 400;
      }
      
      .form-control-modern {
        display: block;
        width: 100%;
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.5;
        color: #1f2937;
        background-color: #ffffff;
        background-clip: padding-box;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      }
      
      .form-control-modern:focus {
        color: #1f2937;
        background-color: #ffffff;
        border-color: #3b82f6;
        outline: 0;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
      
      .form-control-modern.is-invalid {
        border-color: #dc2626;
      }
      
      .form-control-modern.is-invalid:focus {
        border-color: #dc2626;
        box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
      }
      
      .form-select-modern {
        display: block;
        width: 100%;
        padding: 0.75rem 2.5rem 0.75rem 1rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.5;
        color: #1f2937;
        background-color: #ffffff;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right 0.75rem center;
        background-size: 16px 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        cursor: pointer;
      }
      
      .form-select-modern:focus {
        border-color: #3b82f6;
        outline: 0;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
      
      .form-select-modern.is-invalid {
        border-color: #dc2626;
      }
      
      .form-text-modern {
        margin-top: 0.5rem;
        font-size: 0.75rem;
        color: #6b7280;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .invalid-feedback-modern {
        width: 100%;
        margin-top: 0.5rem;
        font-size: 0.75rem;
        color: #dc2626;
        font-weight: 500;
      }
      
      .form-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1.5rem;
      }
      
      .form-grid-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
      }
      
      @media (max-width: 768px) {
        .invoice-form-header {
          padding: 1rem;
        }
        
        .invoice-form-header-content {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;
        }
        
        .form-section {
          padding: 1.5rem;
        }
        
        .form-grid-2 {
          grid-template-columns: 1fr;
        }
        
        .section-header {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }
      }
    `}</style>

      <div className="invoice-form-container">
        {/* Header Section */}
        <div className="invoice-form-header">
          <div className="invoice-form-header-content">
            <div className="invoice-form-icon">
              <i className="bi bi-file-earmark-text"></i>
            </div>
            <div>
              <h2 className="invoice-form-title">Offertinformation</h2>
              <p className="invoice-form-subtitle">
                Fyll i grundläggande information om offerten, lägg till produkter och tjänster som ska ingå i offerten,
                inkludera eventuella meddelanden till kunden. Alla obligatoriska fält måste fyllas i.
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Details Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="bi bi-calendar-event"></i>
            </div>
            <div>
              <h2 className="section-title">Offertdetaljer</h2>
              <p className="section-subtitle">Grundläggande information om offerten</p>
            </div>
          </div>

          <div className="form-grid">
            {/* Invoice Number */}
            <div>
              <label className="form-label-modern">
                Offertnummer
                <span className="form-help">(Automatisk numrering)</span>
              </label>
              <input
                type="text"
                className={`form-control-modern ${getFieldValidation('invoiceNumber').isInvalid ? 'is-invalid' : ''}`}
                value={formData.quoteNumber || ''}
                onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
                disabled={true}
                readOnly={true}
                placeholder="Auto-genereras om tomt"
              />
              {getFieldValidation('invoiceNumber').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.quoteNumber}
                </div>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="form-label-modern">
                Valuta
                <span className="required-asterisk">*</span>
              </label>
              <select
                className={`form-select-modern ${getFieldValidation('currency').isInvalid ? 'is-invalid' : ''}`}
                value={formData.currency || 'SEK'}
                onChange={(e) => handleFieldChange('currency', e.target.value)}
              >
                {currencyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {getFieldValidation('currency').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.currency}
                </div>
              )}
            </div>

            {/* Invoice Date */}
            <div>
              <label className="form-label-modern">
                Offertdatum
                <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                className={`form-control-modern ${getFieldValidation('quoteDate').isInvalid ? 'is-invalid' : ''}`}
                value={formatDate(formData.quoteDate)}
                onChange={(e) => handleDateChange(e, 'quoteDate')}
                required
              />
              {getFieldValidation('quoteDate').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.quoteDate}
                </div>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="form-label-modern">
                Gilltig till
                <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                className={`form-control-modern ${getFieldValidation('validUntil').isInvalid ? 'is-invalid' : ''}`}
                value={formatDate(formData.validUntil)}
                onChange={(e) => handleDateChange(e, 'validUntil')}
                required
              />
              {getFieldValidation('validUntil').isInvalid && (
                <div className="invalid-feedback-modern">
                  {validationErrors.validUntil}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Items Section - Now consistent with other sections */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="bi bi-list-ul"></i>
            </div>
            <div>
              <h2 className="section-title">Produkter & Tjänster</h2>
              <p className="section-subtitle">
                {formData.items?.length > 0
                  ? `${formData.items.length} artikel${formData.items.length !== 1 ? 'ar' : ''} tillagda`
                  : 'Lägg till artiklar som ska ingå i offerten'
                }
              </p>
            </div>
          </div>

          <InvoiceItemListBootstrap
            items={formData.items || []}
            availableArticles={availableArticles}
            onItemsChange={handleItemsChange}
            roundTotal={formData.roundTotal || false}
            onRoundingChange={handleRoundingChange}
            defaultVatRate={formData.defaultVatRate || invoiceSettings?.defaultVatRate || 25}
            currency={formData.currency || 'SEK'}
          />
        </div>

        {/* Notes Section */}
        <div className="form-section">
          <div className="section-header">
            <div className="section-icon">
              <i className="bi bi-chat-text"></i>
            </div>
            <div>
              <h2 className="section-title">Anteckningar</h2>
              <p className="section-subtitle">Lägg till meddelanden som visas på offerten</p>
            </div>
          </div>

          <div>
            <label className="form-label-modern">
              Meddelande till kund
              <span className="form-help">(visas på offerten)</span>
            </label>
            <textarea
              className={`form-control-modern ${getFieldValidation('notes').isInvalid ? 'is-invalid' : ''}`}
              rows="4"
              placeholder="Lägg till information, villkor eller andra meddelanden som ska visas på offerten..."
              value={formData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              maxLength={2000}
            />
            <div className="form-text-modern">
              {/*<span>Lägg till betalningsvillkor, leveransinformation eller andra viktiga meddelanden</span>*/}
              <span>{formData.notes?.length || 0} / 2000 tecken</span>
            </div>
            {getFieldValidation('notes').isInvalid && (
              <div className="invalid-feedback-modern">
                {validationErrors.notes}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default QuoteFormBootstrap;




// import React, { useState, useCallback, useMemo } from 'react';
// import { format } from 'date-fns';
// import QuoteItemListBootstrap from './QuoteItemListBootstrap';
// import AsyncSelect from 'react-select/async';
// import { getValidationState } from '../utils/quoteValidation';

// const QuoteFormBootstrap = ({
//   formData,
//   onFormDataChange,
//   availableItems,
//   customers = [],
//   loading = false,
//   invoiceSettings,
//   onFetchCustomerDetails,
//   validationErrors = {}
// }) => {
//   // Track which fields have been touched for validation display
//   const [touchedFields, setTouchedFields] = useState({});

//   console.log('QuoteFormBootstrap rendered with formData:', formData);

//   // Mark field as touched
//   const handleFieldTouch = useCallback((fieldName) => {
//     setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
//   }, []);

//   // Handle customer selection with real data
//   const handleSelectCustomer = useCallback(async (selectedOption) => {
//     handleFieldTouch('customerid');

//     if (selectedOption) {
//       const customer = customers.find(c => c.id === selectedOption.value);
//       if (customer && onFetchCustomerDetails) {
//         // Fetch full customer details if needed
//         const fullCustomer = await onFetchCustomerDetails(customer.id);
//         onFormDataChange({ customer: fullCustomer || customer });
//       } else {
//         onFormDataChange({ customer });
//       }
//     } else {
//       onFormDataChange({ customer: null });
//     }
//   }, [customers, onFetchCustomerDetails, onFormDataChange, handleFieldTouch]);

//   // Handle items change
//   const handleItemsChange = useCallback((items) => {
//     handleFieldTouch('items');
//     onFormDataChange({ items });
//   }, [onFormDataChange, handleFieldTouch]);

//   // Handle rounding change
//   const handleRoundingChange = useCallback((roundValue) => {
//     onFormDataChange({ roundTotal: roundValue });
//   }, [onFormDataChange]);

//   // Handle date change with validation
//   const handleDateChange = useCallback((e, dateType) => {
//     handleFieldTouch(dateType);
//     try {
//       const date = new Date(e.target.value);
//       if (!isNaN(date.getTime())) {
//         onFormDataChange({ [dateType]: date });
//       }
//     } catch (error) {
//       console.error('Invalid date:', error);
//     }
//   }, [onFormDataChange, handleFieldTouch]);

//   // Handle text field changes
//   const handleFieldChange = useCallback((fieldName, value) => {
//     handleFieldTouch(fieldName);
//     onFormDataChange({ [fieldName]: value });
//   }, [onFormDataChange, handleFieldTouch]);

//   // Format customer display based on type
//   const getCustomerLabel = useCallback((customer) => {
//     if (!customer) return '';

//     if (customer.type === 'company') {
//       const orgNumber = customer.organization_number || customer.orgnumber;
//       return `${customer.name}${orgNumber ? ` (${orgNumber})` : ''}`;
//     } else {
//       const firstName = customer.first_name || customer.firstName || '';
//       const lastName = customer.last_name || customer.lastName || '';
//       const fullName = customer.name || `${firstName} ${lastName}`.trim();
//       return fullName;
//     }
//   }, []);

//   // Filter customers by search term
//   const filterCustomers = useCallback((inputValue) => {
//     const searchTerm = inputValue.toLowerCase();
//     return customers.filter(customer => {
//       const name = (customer.name || '').toLowerCase();
//       const orgNumber = (customer.organization_number || customer.orgnumber || '').toLowerCase();
//       const customerNumber = (customer.number || '').toString().toLowerCase();

//       return name.includes(searchTerm) ||
//         orgNumber.includes(searchTerm) ||
//         customerNumber.includes(searchTerm);
//     });
//   }, [customers]);

//   // Load customer options for the async select
//   const loadCustomerOptions = useCallback((inputValue) => {
//     return new Promise((resolve) => {
//       setTimeout(() => {
//         const filteredCustomers = filterCustomers(inputValue);
//         const options = filteredCustomers.slice(0, 50).map(customer => ({
//           value: customer.id,
//           label: getCustomerLabel(customer),
//           data: customer
//         }));
//         resolve(options);
//       }, 300);
//     });
//   }, [filterCustomers, getCustomerLabel]);

//   // Format date for input
//   const formatDate = useCallback((date) => {
//     if (!date) return '';
//     try {
//       return date instanceof Date && !isNaN(date)
//         ? date.toISOString().split('T')[0]
//         : '';
//     } catch (e) {
//       return '';
//     }
//   }, []);

//   // Get validation props for form fields
//   const getFieldValidation = useCallback((fieldName) => {
//     return getValidationState(fieldName, validationErrors, touchedFields);
//   }, [validationErrors, touchedFields]);

//   // Memoize customer details
//   const customerDetails = useMemo(() => {
//     if (!formData.customer) return null;

//     const customer = formData.customer;
//     const address = customer.addresses?.[0] || {};

//     return {
//       name: customer.name,
//       address: address.street1 || customer.address,
//       zipCode: address.postal_code || customer.zipCode,
//       city: address.city || customer.city,
//       country: address.country || customer.country,
//       email: customer.email,
//       phone: customer.phone,
//       orgNumber: customer.organization_number || customer.orgnumber
//     };
//   }, [formData.customer]);

//   return (
//     <div className="animate__animated animate__fadeIn">
//       <div className="row mb-4">
//         <div className="col-md-6">
//           <label htmlFor="quoteNumber" className="form-label">
//             Offertnummer
//             <span className="text-muted small ms-1">(valfritt)</span>
//           </label>
//           <input
//             type="text"
//             className={`form-control ${getFieldValidation('quoteNumber').isInvalid ? 'is-invalid' : ''}`}
//             id="quoteNumber"
//             value={formData.quoteNumber || ''}
//             onChange={(e) => handleFieldChange('quoteNumber', e.target.value)}
//             placeholder="Lämna tomt för automatisk numrering"
//           />
//           {getFieldValidation('quoteNumber').isInvalid && (
//             <div className="invalid-feedback">
//               {validationErrors.quoteNumber}
//             </div>
//           )}
//         </div>

//         <div className="col-md-6">
//           <div className="row">
//             <div className="col-md-6">
//               <label htmlFor="quoteDate" className="form-label">
//                 Offertdatum <span className="text-danger">*</span>
//               </label>
//               <input
//                 type="date"
//                 className={`form-control ${getFieldValidation('quoteDate').isInvalid ? 'is-invalid' : ''}`}
//                 id="quoteDate"
//                 value={formatDate(formData.quoteDate)}
//                 onChange={(e) => handleDateChange(e, 'quoteDate')}
//                 required
//               />
//               {getFieldValidation('quoteDate').isInvalid && (
//                 <div className="invalid-feedback">
//                   {validationErrors.quoteDate}
//                 </div>
//               )}
//             </div>

//             <div className="col-md-6">
//               <label htmlFor="validUntil" className="form-label">
//                 Giltig till <span className="text-danger">*</span>
//               </label>
//               <input
//                 type="date"
//                 className={`form-control ${getFieldValidation('validUntil').isInvalid ? 'is-invalid' : ''}`}
//                 id="validUntil"
//                 value={formatDate(formData.validUntil)}
//                 onChange={(e) => handleDateChange(e, 'validUntil')}
//                 required
//               />
//               {getFieldValidation('validUntil').isInvalid && (
//                 <div className="invalid-feedback">
//                   {validationErrors.validUntil}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="mb-4">
//         <label htmlFor="customer" className="form-label">
//           Kund <span className="text-danger">*</span>
//         </label>
//         <AsyncSelect
//           cacheOptions
//           defaultOptions={customers.slice(0, 20).map(customer => ({
//             value: customer.id,
//             label: getCustomerLabel(customer),
//             data: customer
//           }))}
//           value={formData.customer ? {
//             value: formData.customer.id,
//             label: getCustomerLabel(formData.customer)
//           } : null}
//           loadOptions={loadCustomerOptions}
//           onChange={handleSelectCustomer}
//           onBlur={() => handleFieldTouch('customerid')}
//           isClearable
//           placeholder="Sök kund..."
//           noOptionsMessage={() => "Inga kunder hittades"}
//           loadingMessage={() => "Söker kunder..."}
//           isDisabled={loading}
//           classNamePrefix="react-select"
//           className={getFieldValidation('customerid').isInvalid ? 'is-invalid' : ''}
//           styles={{
//             control: (baseStyles, state) => ({
//               ...baseStyles,
//               borderColor: getFieldValidation('customerid').isInvalid
//                 ? '#dc3545'
//                 : state.isFocused
//                   ? '#80bdff'
//                   : '#ced4da',
//               boxShadow: state.isFocused
//                 ? getFieldValidation('customerid').isInvalid
//                   ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
//                   : '0 0 0 0.2rem rgba(8, 154, 25, 0.25)'
//                 : null,
//               '&:hover': {
//                 borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
//               }
//             }),
//             menu: (baseStyles) => ({
//               ...baseStyles,
//               zIndex: 1000
//             })
//           }}
//         />
//         {getFieldValidation('customerid').isInvalid && (
//           <div className="invalid-feedback d-block">
//             {validationErrors.customerid || validationErrors.customer}
//           </div>
//         )}
//       </div>

//       {customerDetails && (
//         <div className="mb-4 p-3 rounded border-start border-4 border-primary bg-light animate__animated animate__fadeIn">
//           <h6 className="mb-2">Kunduppgifter</h6>
//           <div className="small text-muted">
//             <p className="mb-1"><strong>{customerDetails.name}</strong></p>
//             {customerDetails.address && <p className="mb-1">{customerDetails.address}</p>}
//             {customerDetails.zipCode && customerDetails.city && (
//               <p className="mb-1">{customerDetails.zipCode} {customerDetails.city}</p>
//             )}
//             {customerDetails.country && <p className="mb-1">{customerDetails.country}</p>}
//             {customerDetails.email && (
//               <p className="mb-1">
//                 <i className="bi bi-envelope me-1"></i> {customerDetails.email}
//               </p>
//             )}
//             {customerDetails.phone && (
//               <p className="mb-1">
//                 <i className="bi bi-phone me-1"></i> {customerDetails.phone}
//               </p>
//             )}
//             {customerDetails.orgNumber && (
//               <p className="mb-0">
//                 <i className="bi bi-building me-1"></i> Org.nr: {customerDetails.orgNumber}
//               </p>
//             )}
//           </div>
//         </div>
//       )}

//       <QuoteItemListBootstrap
//         items={formData.items || []}
//         availableItems={availableItems}
//         onItemsChange={handleItemsChange}
//         roundTotal={formData.roundTotal || false}
//         onRoundingChange={handleRoundingChange}
//         defaultVatRate={formData.defaultVatRate || invoiceSettings?.defaultVatRate || 25}
//         currency={formData.currency || 'SEK'}
//       />

//       <div className="mb-3 mt-4">
//         <label htmlFor="notes" className="form-label">
//           Anteckningar
//           <span className="text-muted small ms-1">(visas på offerten)</span>
//         </label>
//         <textarea
//           className={`form-control ${getFieldValidation('notes').isInvalid ? 'is-invalid' : ''}`}
//           id="notes"
//           rows="4"
//           placeholder="Lägg till anteckningar för kunden (villkor, information, etc.)"
//           value={formData.notes || ''}
//           onChange={(e) => handleFieldChange('notes', e.target.value)}
//           maxLength={2000}
//         />
//         <div className="form-text">
//           {formData.notes?.length || 0} / 2000 tecken
//         </div>
//         {getFieldValidation('notes').isInvalid && (
//           <div className="invalid-feedback">
//             {validationErrors.notes}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default QuoteFormBootstrap;
